import {
    HStack,
    VStack,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Box,
    IconButton,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { QueueStore } from '../../../queues/store/queue-store';
import { useQueueStore } from '../../../queues/context/use-queue-store';
import { QueueFilterMultiselect } from '../../../common/components/queues-filter-select';
import { useEventQueueFilterOptions } from '../../get-event-filter-options';
import { ColumnDefinitionEnum } from '@mark43/cad-api';

export const EventQueueFilterPopover = observer(
    function EventQueueFilterPopover(): React.ReactElement {
        const queueStore = useQueueStore() as QueueStore;

        const {
            additionalCfsOptions: addCfsOptions,
            cfsOptions,
            priorityOptions,
            alarmLevelOptions,
            eventLabelOptions,
            dispatchAreaOptions,
            agencyOptions,
            eventPrimarySubdivisionOptions,
        } = useEventQueueFilterOptions();

        const allSelectedEventQueueFilterValues: number[] = (
            queueStore.workspacePanel.queue.eventQueueFilters ?? []
        )
            .filter(
                (eventQueueFilter) =>
                    eventQueueFilter.columnDefinition !== ColumnDefinitionEnum.ADDITIONAL_CFS.name
            )
            .map((eventQueueFilter) => eventQueueFilter.entityId);

        const allSelectedAdditionalCfsFilterValues: number[] = (
            queueStore.workspacePanel.queue.eventQueueFilters ?? []
        )
            .filter(
                (eventQueueFilter) =>
                    eventQueueFilter.columnDefinition === ColumnDefinitionEnum.ADDITIONAL_CFS.name
            )
            .map((eventQueueFilter) => eventQueueFilter.entityId);

        const allOptions = useMemo(() => {
            return [
                ...dispatchAreaOptions,
                ...agencyOptions,
                ...alarmLevelOptions,
                ...cfsOptions,
                ...eventLabelOptions,
                ...priorityOptions,
                ...eventPrimarySubdivisionOptions,
            ];
        }, [
            dispatchAreaOptions,
            agencyOptions,
            alarmLevelOptions,
            cfsOptions,
            eventLabelOptions,
            priorityOptions,
            eventPrimarySubdivisionOptions,
        ]);

        const additionalCfsOptions = useMemo(() => {
            return addCfsOptions;
        }, [addCfsOptions]);

        const onChangeEventQueueFilters = useCallback(
            (entityIds: number[]) => {
                const selectedOptions = allOptions
                    .filter((option) => entityIds.includes(option.value))
                    .map((option) => ({
                        value: option.value,
                        label: option.label,
                        columnDefinition: option.columnDefinition,
                    }));

                queueStore.setEventQueueFilters(selectedOptions);
            },
            [queueStore, allOptions]
        );

        const onChangeAdditionalCfsFilters = useCallback(
            (entityIds: number[]) => {
                const selectedOptions = additionalCfsOptions
                    .filter((option) => entityIds.includes(option.value))
                    .map((option) => ({
                        value: option.value,
                        label: option.label,
                        columnDefinition: ColumnDefinitionEnum.ADDITIONAL_CFS.name,
                    }));

                queueStore.upsertEventQueueFilters(selectedOptions, 'ADDITIONAL_CFS');
            },
            [queueStore, additionalCfsOptions]
        );

        const clearAll = useCallback(() => {
            onChangeEventQueueFilters([]);
            onChangeAdditionalCfsFilters([]);
        }, [onChangeEventQueueFilters, onChangeAdditionalCfsFilters]);

        const isActive = !!queueStore.workspacePanel.queue.eventQueueFilters.length;

        const totalNumberOfFiltersSelected =
            allSelectedEventQueueFilterValues.length + allSelectedAdditionalCfsFilterValues.length;

        const showClearAll = useMemo(() => totalNumberOfFiltersSelected > 0, [
            totalNumberOfFiltersSelected,
        ]);

        const filterComponentsMap = useMemo(() => {
            return [
                {
                    label: ColumnDefinitionEnum.ADDITIONAL_CFS.displayName,
                    attributeOptions: additionalCfsOptions,
                    values: allSelectedAdditionalCfsFilterValues,
                    onChange: onChangeAdditionalCfsFilters,
                },
                {
                    label: ColumnDefinitionEnum.ALARM_LEVEL.displayName,
                    attributeOptions: alarmLevelOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.DISPATCH_AREA.displayName,
                    attributeOptions: dispatchAreaOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.AGENCY.displayName,
                    attributeOptions: agencyOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.CFS_NUMBER_AND_TEXT.displayName,
                    attributeOptions: cfsOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.EVENT_LABELS.displayName,
                    attributeOptions: eventLabelOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.PRIORITY.displayName,
                    attributeOptions: priorityOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
                {
                    label: ColumnDefinitionEnum.BEAT.displayName,
                    attributeOptions: eventPrimarySubdivisionOptions,
                    values: allSelectedEventQueueFilterValues,
                    onChange: onChangeEventQueueFilters,
                },
            ].sort((a, b) => a.label.localeCompare(b.label));
        }, [
            additionalCfsOptions,
            alarmLevelOptions,
            dispatchAreaOptions,
            agencyOptions,
            cfsOptions,
            eventLabelOptions,
            priorityOptions,
            allSelectedAdditionalCfsFilterValues,
            eventPrimarySubdivisionOptions,
            allSelectedEventQueueFilterValues,
            onChangeAdditionalCfsFilters,
            onChangeEventQueueFilters,
        ]);

        return (
            <Popover hasArrow={false}>
                <PopoverTrigger asChild>
                    <IconButton
                        aria-label="filter by event queue filters"
                        icon="Filter"
                        isActive={isActive}
                        variant="ghost"
                        data-testid="event-queue-filter-popover-button"
                    />
                </PopoverTrigger>
                <PopoverContent align="start" style={{ paddingRight: 0 }}>
                    <VStack data-testid="event-queue-filter-popover-body" pr={0} pb={0}>
                        <HStack justifyContent="flex-end" alignItems="center" w="100%" pr="22px">
                            {showClearAll && (
                                <Button
                                    variant="ghost"
                                    onClick={clearAll}
                                    data-testid="event-queue-clear-all-button"
                                >
                                    Clear All
                                </Button>
                            )}
                        </HStack>
                        <Box
                            pr="12px"
                            minWidth="350px"
                            maxWidth="350px"
                            maxHeight="500px"
                            overflowY="auto"
                            textTransform="none"
                            className="custom-scrollbar"
                        >
                            {filterComponentsMap.map((filter) => (
                                <QueueFilterMultiselect
                                    key={filter.label}
                                    label={filter.label}
                                    attributeOptions={filter.attributeOptions}
                                    values={filter.values}
                                    onChange={(values) => filter.onChange(values as number[])}
                                    data-testid={`event-queue-filter-${filter.label
                                        .replace(/\s+/g, '-')
                                        .toLowerCase()}`}
                                />
                            ))}
                        </Box>
                    </VStack>
                </PopoverContent>
            </Popover>
        );
    }
);
